import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Flex, Spin, Tabs, TabsProps } from "antd";
import PageHeading from "components/PageHeading.component";
import { useQuery } from "@tanstack/react-query";
import UserProfileCard from "components/profile/UserProfile.component";
import { bookingsByCustomerV2Options } from "hooks/queries/booking-v2";
import { useGetCustomerLoginCode } from "hooks/queries/customer";
import {
  useRegenerateCustomerLoginLink,
  useRegenerateCustomerLoginCode,
} from "hooks/mutations/customer";
import { useGetUserById } from "hooks/queries/user";
import { useTabParam } from "hooks/useTabParam";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { UserType } from "shared/types/enum";
import { ScheduleResumeDraft } from "shared/utils/scheduleResumeDraft";
import { isUserDeactivated } from "shared/utils";
import { RestrictedBanner } from "components/capability";
import { CustomerBooking } from "../../components/bookings";
import CustomerPackagePage from "./CustomerPackage";
import CustomerTransactionsPage from "./CustomerTransactions";
import RewardTab from "./RewardTab.component";

const CustomerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  // Arrived here mid-schedule-creation or mid-booking (see
  // CreateScheduleDrawer.tsx / NewBookingModal.component.tsx /
  // AddToWaitlistModal.component.tsx) — relayed onward unexamined via its
  // own `returnTo`; this page doesn't otherwise care what's inside it.
  const scheduleDraft = (
    location.state as { scheduleDraft?: ScheduleResumeDraft } | null
  )?.scheduleDraft;
  // Must list EVERY key in `items` below — useTabParam sanitizes anything
  // outside this list back to the default key, so a missing entry silently
  // makes that tab unselectable. "6" is Transaksi: it was renumbered off "5"
  // when Reward took that key (server notification links deep-link to
  // ?tab=5), and this list wasn't updated to match.
  const { activeKey, onChange } = useTabParam("1", "tab", [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
  ]);
  const {
    data: customer,
    isLoading,
    isError,
  } = useGetUserById(customerId ?? "", UserType.Customer);
  const { data: bookings = [], isLoading: isBookingLoading } = useQuery(
    bookingsByCustomerV2Options(customer?.id)
  );
  const { data: loginCredentials } = useGetCustomerLoginCode(customerId ?? "");
  const regenerateLoginLink = useRegenerateCustomerLoginLink();
  const regenerateLoginCode = useRegenerateCustomerLoginCode();

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: translate("tabs.profile"),
      children: customer && (
        <UserProfileCard
          user={customer}
          thisUserType={UserType.Customer}
          loginCode={loginCredentials?.code ?? null}
          loginLink={loginCredentials?.link ?? null}
          onRegenerateLoginLink={() =>
            customerId && regenerateLoginLink.mutate(customerId)
          }
          isRegeneratingLoginLink={regenerateLoginLink.isPending}
          onRegenerateLoginCode={() =>
            customerId && regenerateLoginCode.mutate(customerId)
          }
          isRegeneratingLoginCode={regenerateLoginCode.isPending}
        />
      ),
    },
    {
      key: "2",
      label: translate("tabs.packages"),
      children: customer && <CustomerPackagePage customer={customer} />,
    },
    {
      key: "4",
      label: translate("tabs.membership"),
      children: customer && (
        <CustomerPackagePage customer={customer} isMembership />
      ),
    },
    {
      key: "6",
      label: translate("tabs.transactions"),
      children: customer && <CustomerTransactionsPage customer={customer} />,
    },
    {
      key: "3",
      label: translate("tabs.bookings"),
      children: customer && (
        <CustomerBooking bookings={bookings} isLoading={isBookingLoading} />
      ),
    },
    {
      // Key "5" is load-bearing: server notification links deep-link to
      // /customer/:id?tab=5. The pane is now Reward = Loyalty Card + Referral
      // sub-tabs (the `reward` URL param).
      key: "5",
      label: translate("tabs.reward"),
      children: customer && <RewardTab customer={customer} />,
    },
  ];

  // If retrieving is error throw to not found.
  useEffect(() => {
    if (isError) navigate(PATHS.NOT_FOUND);
    // eslint-disable-next-line
  }, [isError]);

  return (
    <>
      {isLoading ? (
        <Flex
          style={{
            justifyContent: "center",
            alignItems: "center",
            height: "100vh ",
          }}
        >
          <Spin />
        </Flex>
      ) : (
        <>
          <PageHeading
            title={
              scheduleDraft ? (
                <Flex gap={12} align="center">
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() =>
                      navigate(scheduleDraft.returnTo, {
                        state: { scheduleDraft },
                      })
                    }
                    aria-label={translate("schedule.back_to_schedule")}
                  />
                  {translate("title.customer_detail")}
                </Flex>
              ) : (
                translate("title.customer_detail")
              )
            }
          />
          {/* Deactivated here: read-only until Aktifkan. The banner stands in
              for per-control hover reasons, which the house rule leaves out. */}
          {customer && isUserDeactivated(customer, UserType.Customer) && (
            <div style={{ marginBottom: 16 }}>
              <RestrictedBanner title={translate("client_status.banner")} />
            </div>
          )}
          <Tabs
            activeKey={activeKey}
            size="small"
            items={items}
            onChange={onChange}
          />
        </>
      )}
    </>
  );
};

export default CustomerDetails;
